// src/store/mfaThunks.ts
/**
 * The request half of `mfaSlice`, kept out of the slice on purpose.
 *
 * `securityApi` imports the store; the store imports the slice's reducer. Putting this function in
 * `mfaSlice.ts` would close that circle — store → mfaSlice → securityApi → store — and a circular
 * import in a CRA bundle fails silently, leaving one module half-initialised at run time rather than
 * erroring at build time. Nothing the store imports pulls this file in, so the cycle never forms.
 *
 * `RootState` is imported as a TYPE ONLY, which babel erases outright, so it adds no runtime edge
 * back to the store either.
 */
import { ThunkAction, UnknownAction } from '@reduxjs/toolkit';
import { errText } from '../api/errors';
import { fetchMfaStatus } from '../api/securityApi';
import { statusFailed, statusLoaded, statusLoading } from './mfaSlice';
import type { RootState } from './store';

export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, RootState, unknown, UnknownAction>;

const STATUS_LOAD_FAILED = 'Nepodarilo sa overiť stav dvojstupňového overenia.';

/**
 * Fetch `GET /v1/mfa/status` into the slice.
 *
 * Safe to call from any admin screen's mount effect, which is the point — several of them do, and a
 * page can mount two gated components at once:
 *
 * - It is a NO-OP for anyone who is not a signed-in admin holding a token. `/v1/mfa/status` is
 *   `hasRole('ADMIN')`, so calling it as a member is a guaranteed 403; asking would achieve nothing
 *   except a refusal in the console for every ordinary visitor.
 * - It DEDUPES on the token. A load already in flight for this token is never doubled, even with
 *   `force` — two concurrent answers can only race each other. A status already `ready` for this
 *   token is skipped unless `force` says otherwise; `error` is never skipped, so a retry button
 *   needs no flag.
 * - It DROPS A LATE ANSWER. Between the request and its reply the user may have logged out or logged
 *   in again; writing that answer would attach one session's `sessionVerified` to another's token.
 *   The token is re-read from the store after the await and the result is discarded if it moved.
 *
 * Failure is recorded, never thrown: `statusFailed` makes the gate fail open with a banner rather
 * than lock an admin out of their own pages over a network blip. The server still refuses anything
 * that genuinely requires step-up.
 */
export const loadMfaStatus =
    ({ force = false }: { force?: boolean } = {}): AppThunk<Promise<void>> =>
    async (dispatch, getState) => {
        const { auth, mfa } = getState();
        const token = auth.accessToken;
        if (!auth.isAuthenticated || !token || !auth.user?.isAdmin) {
            return;
        }
        if (mfa.forToken === token) {
            if (mfa.loadState === 'loading') {
                return;
            }
            if (!force && mfa.loadState === 'ready') {
                return;
            }
        }

        dispatch(statusLoading({ token }));
        try {
            const status = await fetchMfaStatus();
            if (getState().auth.accessToken !== token) {
                return;
            }
            dispatch(statusLoaded({ token, status }));
        } catch (err) {
            if (getState().auth.accessToken !== token) {
                return;
            }
            dispatch(statusFailed({ token, message: errText(err, STATUS_LOAD_FAILED) }));
        }
    };
